import type { ConnectionAuthType, PlatformId } from './connection.ts';

/**
 * Metadata the wizard uses to render platform cards, permission notices and
 * credential forms. The registry stays frontend-only — the backend's own
 * platform list is the source of truth for availability, this file is just
 * presentation.
 */

export interface CredentialFieldSpec {
  /** Key sent to the backend in `credentials[key]`. */
  key: string;
  /** i18n key for the field label. */
  labelKey: string;
  /** i18n key for optional helper text. */
  helpKey?: string;
  type: 'text' | 'password' | 'url';
  required?: boolean;
}

export interface PlatformSpec {
  id: PlatformId;
  /** Short display name — not translated, it's a brand name. */
  displayName: string;
  /** i18n key for the one-line pitch shown on the platform picker. */
  taglineKey: string;
  /** i18n key for the longer "what Scriptix will be able to do" copy. */
  permissionCopyKey: string;
  /** OAuth or credentials form. */
  authType: ConnectionAuthType;
  /** For credentials platforms: the fields the wizard renders. */
  credentialFields?: CredentialFieldSpec[];
  /**
   * Google's consent screen is scoped to full video edit/delete because
   * YouTube does not expose a caption-only scope. The wizard shows this
   * i18n key as a warning before the user is sent to Google.
   */
  oauthWarningKey?: string;
  /**
   * When true the picker renders the card as "coming soon": the radio
   * is disabled, no selection is possible, and a badge is shown.
   * Used for iBabs (#4102) until the Arbor contract is finalised.
   */
  comingSoon?: boolean;
}

export const PLATFORMS: PlatformSpec[] = [
  {
    id: 'youtube',
    displayName: 'YouTube',
    taglineKey: 'publishing-platform-youtube-tagline',
    permissionCopyKey: 'publishing-platform-youtube-permissions',
    authType: 'oauth',
    oauthWarningKey: 'publishing-platform-youtube-oauth-warning'
  },
  {
    id: 'vimeo',
    displayName: 'Vimeo',
    taglineKey: 'publishing-platform-vimeo-tagline',
    permissionCopyKey: 'publishing-platform-vimeo-permissions',
    authType: 'oauth'
  },
  {
    id: 'ibabs',
    displayName: 'iBabs',
    taglineKey: 'publishing-platform-ibabs-tagline',
    permissionCopyKey: 'publishing-platform-ibabs-permissions',
    authType: 'credentials',
    comingSoon: true,
    credentialFields: [
      {
        key: 'site_url',
        labelKey: 'publishing-ibabs-site-url',
        helpKey: 'publishing-ibabs-site-url-help',
        type: 'url',
        required: true
      },
      {
        key: 'api_key',
        labelKey: 'publishing-ibabs-api-key',
        type: 'password',
        required: true
      }
    ]
  }
];

export function getPlatformSpec(platformId: PlatformId): PlatformSpec | null {
  return PLATFORMS.find(p => p.id === platformId) ?? null;
}
